import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {apiClient} from 'common/util/api-client';
import {EmbedConnectResults} from 'app_api/embed/types';
import {getProjectId} from 'common/report/util';
import {setEmbedTrackingProps} from 'common/report/report-tracker';
import {track, TrackEvents} from 'media/js/auth/tracking';

import {template} from './template';
import css from './index.inline.styl';

type EmbedUnauthenticatedState = {
  attemptConnectionError: string;
  code: string;
  showAuthButtonFallback: boolean;
  storageAccessError: string;
};

export class EmbedUnauthenticated extends Component<EmbedUnauthenticatedState> {
  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        attemptConnectionError: null,
        code: null,
        showAuthButtonFallback: false,
        storageAccessError: null,
      },
    };
  }

  generateCode(length: number) {
    const characters = `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=`;
    let result = ``;
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  async handleLoginButtonClick() {
    track(TrackEvents.EmbedLoginClick);
    if (typeof document.requestStorageAccess === `function`) {
      try {
        await document.requestStorageAccess();
        this.update({storageAccessError: null});
      } catch (err) {
        this.update({storageAccessError: `Storage access is required for Mixpanel to work in embedded mode.`});
        return;
      }
    }
    // Method can fail on sandboxed iframes preventing fallback from showing so needs to be caught
    try {
      window.open(`/embed/connect?code=${encodeURIComponent(this.state.code)}`, `_blank`);
    } catch (err) {
      console.error(err);
    } finally {
      this.update({showAuthButtonFallback: true});
    }
  }

  async attemptConnection() {
    try {
      this.update({attemptConnectionError: null});
      const results = await apiClient.get<EmbedConnectResults>(
        `/api/app/embed/connect/${encodeURIComponent(this.state.code)}?project_id=${getProjectId()}`,
      );
      if (results.connected) {
        window.location.reload();
      } else {
        setTimeout(() => this.attemptConnection(), 1000);
      }
    } catch (err) {
      this.update({attemptConnectionError: err.message});
      console.error(err);
      setTimeout(() => this.attemptConnection(), 1000);
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    setEmbedTrackingProps();
    this.update({code: this.generateCode(8)});
    this.attemptConnection();
  }
}

export const EmbedUnauthenticatedEl = defineAndMakeJsxFactory(`mp-embed-unauthenticated`, EmbedUnauthenticated);
